import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SystemSettingDTO,
    useDeleteSystemSettingApi,
    useSaveSystemSettingApi,
    useSearchSystemSettingApi,
} from '../../../api/system-setting-api';
import SvgAdd from '../../../assets/icons/Add';
import SvgFilterList from '../../../assets/icons/FilterList';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikTextField } from '../../../components/formik';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSystemSettingModal } from './save-system-setting-modal';

export function SystemSettingApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSystemSetting, setSelectedSystemSetting] = useState<
        SystemSettingDTO | undefined
    >();
    const systemSettingInit = useSearchSystemSettingApi();
    const saveSystemSetting = useSaveSystemSettingApi();
    const deleteSystemSetting = useDeleteSystemSettingApi();

    const { result, error, busy, initialized, initializing } = systemSettingInit;
    const { busy: busySaving } = saveSystemSetting;

    const columns: IBasicTableColumn<SystemSettingDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
            },
            {
                Header: <T>value</T>,
                name: 'value',
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        systemSettingInit.post(null, {});
    }, [initialized, initializing, error, systemSettingInit]);

    const handleDelete = (systemSetting: SystemSettingDTO) => {
        bd.msgbox(
            translate('delete-system-setting'),
            translate('confirm-delete-system-setting'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSystemSetting.remove(
                            systemSetting,
                            () => {
                                systemSettingInit.updateResult({
                                    ...result,
                                    systemSettings: result.systemSettings.filter(
                                        b => b.id !== systemSetting.id
                                    ),
                                });
                                notify.dark(
                                    translate('selected-system-setting-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedSystemSetting(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (systemSetting: SystemSettingDTO) => {
        saveSystemSetting.post(
            null,
            systemSetting,
            res => {
                if (selectedSystemSetting) {
                    systemSettingInit.updateResult({
                        ...result,
                        systemSettings: result.systemSettings.map(b =>
                            b.id === systemSetting.id ? res.systemSetting : b
                        ),
                    });
                } else {
                    systemSettingInit.updateResult({
                        ...result,
                        systemSettings: [res.systemSetting, ...result.systemSettings],
                    });
                }

                setShowModal(false);
                setSelectedSystemSetting(undefined);
                notify.success(
                    selectedSystemSetting
                        ? translate('selected-system-setting-updated-successfully')
                        : translate('new-system-setting-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => systemSettingInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="system-setting"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-system-setting</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedSystemSetting(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100 p-3">
                    <Formik
                        initialValues={{ groupId: '' }}
                        onSubmit={values => {
                            systemSettingInit.post(null, { groupId: values.groupId });
                        }}
                    >
                        <Form>
                            <bd.Flex wrap align="center">
                                <FormikTextField
                                    items={[
                                        { id: '', title: '' },
                                        ...result.systemSettingGroups.map(x => ({
                                            id: x.id,
                                            title: translate(x.title),
                                        })),
                                    ]}
                                    name="groupId"
                                    label="group"
                                    width="11rem"
                                />
                                <bd.Button variant="contained" type="submit" className="mt-2">
                                    <SvgFilterList />
                                    <T>filter</T>
                                </bd.Button>
                            </bd.Flex>
                        </Form>
                    </Formik>
                    <bd.BasicTable
                        hover
                        columns={columns}
                        className="mx-n3"
                        data={result.systemSettings}
                        onRowClick={({ row }) => {
                            setSelectedSystemSetting(row);
                            setShowModal(true);
                        }}
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    <SaveSystemSettingModal
                        show={showModal}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        groups={result.systemSettingGroups}
                        selectedSystemSetting={selectedSystemSetting}
                        allowEdit={result.allowEdit}
                        busy={busySaving}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSystemSetting(undefined);
                        }}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
